import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Armchair,
  ArrowDown,
  ArrowUpDown,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUp,
  Clock,
  Footprints,
  Layers,
  LoaderCircle,
  MapPin,
  Navigation,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Truck,
  Warehouse,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgWarehouses } from '../../hooks/useOrgWarehouses';
import { AddressSearch, type SelectedPlace } from '../../components/ui/AddressSearch';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { api } from '../../lib/api';
import {
  LogisticsData,
  MoveTiming,
  QuoteAddress,
  RentCastPropertyData,
  ResidentialPropertyDetails,
  ResidentialPropertyType,
  ShiftSetting,
  DEFAULT_SHIFT_SETTINGS,
  TravelTimeRate,
  TravelToJob,
  WaypointType,
  WarehouseTransitLeg,
  Warehouse as WarehouseType,
  formatDistance,
  formatDuration,
} from '../../types/admin';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import { DateTimePicker } from '../../components/ui/DateTimePicker';
import { resolveOrgTravelTimeRate } from '@iomi/shared';

// ── Constants ─────────────────────────────────────────────────────────────────

// Shift type options are now loaded from org settings (see ShiftSetting type)

const PROPERTY_TYPES: { value: ResidentialPropertyType; label: string }[] = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'manufactured', label: 'Manufactured' },
  { value: 'multi_family', label: 'Multi-Family' },
];

const RES_PROP_DEFAULTS: ResidentialPropertyDetails = {
  bedrooms: 2,
  bathrooms: 1,
  basement: false,
  stories: 0,
  elevator: false,
  garageSpaces: 0,
  totalRoomCount: 0,
  pool: false,
  longCarry: false,
};
const WAYPOINTS: WaypointType[] = ['Truck', 'Stairs', 'Elevator'];
const MOVE_TYPES = [
  { value: 'local' as const, label: 'Local' },
  { value: 'intrastate' as const, label: 'Long Distance (In-State)' },
  { value: 'interstate' as const, label: 'Out of State' },
];

/**
 * Parse city and state from a Google Places address string.
 * Google Places Autocomplete descriptions vary:
 *   "123 Main St, Chicago, IL 60601, USA"   (with zip)
 *   "123 Main St, Chicago, IL, USA"          (without zip)
 */
function parseCityState(address: string): { city: string; state: string } | null {
  const parts = address.split(',').map(s => s.trim());
  if (parts.length < 3) return null;
  // State (+ optional zip) is usually the second-to-last part (before country)
  const stateZip = parts[parts.length - 2];
  const stateMatch = stateZip.match(/^([A-Z]{2})(\s|$)/);
  if (!stateMatch) return null;
  const state = stateMatch[1];
  // City is usually the part before state+zip
  const city = parts[parts.length - 3];
  return { city: city.toLowerCase(), state: state.toUpperCase() };
}

/**
 * Infer move type from addresses and transit data.
 * Rules (in order):
 * 1. Different states → "Out of State" (interstate)
 * 2. Within org's local move radius → "Local"
 * 3. Same state, outside radius → "Long Distance (In-State)" (intrastate)
 *
 * When transit data is not yet available, falls back to city comparison.
 */
function inferMoveType(
  originAddr: string,
  destAddr: string,
  transitData?: { distanceMeters: number; durationSeconds: number } | null,
  org?: { localMoveRadiusType?: 'distance' | 'time'; localMoveRadiusValue?: number } | null,
): 'local' | 'intrastate' | 'interstate' | null {
  const orig = parseCityState(originAddr);
  const dest = parseCityState(destAddr);
  if (!orig || !dest) return null;

  // 1. Different states → Out of State
  if (orig.state !== dest.state) return 'interstate';

  // 2. If transit data is available, check against org's local move radius
  if (transitData) {
    const radiusType = org?.localMoveRadiusType ?? 'distance';
    const radiusValue = org?.localMoveRadiusValue ?? 50;

    if (radiusType === 'distance') {
      const distanceMiles = transitData.distanceMeters / 1609.344;
      return distanceMiles <= radiusValue ? 'local' : 'intrastate';
    } else {
      const durationHours = transitData.durationSeconds / 3600;
      return durationHours <= radiusValue ? 'local' : 'intrastate';
    }
  }

  // 3. Fallback when transit not available: same city = local, different city = intrastate
  if (orig.city !== dest.city) return 'intrastate';
  return 'local';
}

// ── Logistics form state ──────────────────────────────────────────────────────

interface LogisticsFormState {
  strides1: string;
  waypoint: WaypointType;
  stories: string;
  strides2: string;
  loadingDock: boolean;
}

const DEFAULT_LOGISTICS: LogisticsFormState = {
  strides1: '',
  waypoint: 'Truck',
  stories: '',
  strides2: '',
  loadingDock: false,
};

function fromLogisticsData(data?: LogisticsData): LogisticsFormState {
  return {
    strides1: data?.strides1 ? String(data.strides1) : '',
    waypoint: data?.waypoint ?? 'Truck',
    stories: data?.stories ? String(data.stories) : '',
    strides2: data?.strides2 ? String(data.strides2) : '',
    loadingDock: data?.loadingDock ?? false,
  };
}

function toLogisticsData(
  state: LogisticsFormState,
  includeLoadingDock: boolean,
): LogisticsData {
  const s1 = parseInt(state.strides1, 10);
  const s2 = parseInt(state.strides2, 10);
  const sto = parseInt(state.stories, 10);
  const hasS1 = !isNaN(s1) && s1 > 0;
  const hasS2 = state.waypoint !== 'Truck' && !isNaN(s2) && s2 > 0;
  const hasSto = state.waypoint === 'Stairs' && !isNaN(sto) && sto > 0;
  return {
    strides1: hasS1 ? s1 : undefined,
    distanceFeet1: hasS1 ? s1 * 3 : undefined,
    waypoint: state.waypoint,
    stories: hasSto ? sto : undefined,
    strides2: hasS2 ? s2 : undefined,
    distanceFeet2: hasS2 ? s2 * 3 : undefined,
    loadingDock: includeLoadingDock ? state.loadingDock : undefined,
  };
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

function buildAddress(
  place: SelectedPlace | null,
  floor: string,
  office: string,
  residentialProperty?: ResidentialPropertyDetails,
  importedPropertyData?: RentCastPropertyData | null,
  apt?: string,
): QuoteAddress | undefined {
  if (!place) return undefined;
  return {
    address: place.address,
    placeId: place.placeId,
    floor: floor.trim() || undefined,
    officeNumber: office.trim() || undefined,
    apt: apt?.trim() || undefined,
    residentialProperty,
    importedPropertyData: importedPropertyData ?? undefined,
  };
}

function fmtSecs(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
}

// ── Field primitives ──────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-widest text-slate">
      {children}
      {optional && <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>}
    </label>
  );
}

function TextInput({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      id={id}
      type="text"
      autoComplete="off"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                 text-small text-midnight placeholder:text-slate
                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                 transition-colors duration-150"
    />
  );
}

function DateInput({
  id,
  value,
  onChange,
  label,
  optional,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  label: string;
  optional?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Split combined value into date and time parts
  const datePart = value ? value.split('T')[0] : '';
  const timePart = value && value.includes('T') ? value.split('T')[1] : '';
  const showTime = !!timePart;

  const formatted = datePart ? (() => {
    const [y, m, d] = datePart.split('-').map(Number);
    const dateStr = new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
    if (timePart) {
      const [h, min] = timePart.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${dateStr} at ${h12}:${min.toString().padStart(2, '0')} ${ampm}`;
    }
    return dateStr;
  })() : null;

  const handleDateChange = (newDate: string) => {
    if (!newDate) { onChange(''); return; }
    onChange(timePart ? `${newDate}T${timePart}` : newDate);
  };

  const handleTimeToggle = () => {
    if (showTime) {
      // Remove time
      onChange(datePart);
    } else if (datePart) {
      // Add default time
      onChange(`${datePart}T09:00`);
    }
  };

  const handleTimeChange = (newTime: string) => {
    if (!newTime) { onChange(datePart); return; }
    onChange(`${datePart}T${newTime}`);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel htmlFor={id} optional={optional}>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <div
          className="relative h-11 flex-1 rounded-lg border border-stone bg-white flex items-center px-3 gap-2
                     focus-within:border-plum focus-within:ring-2 focus-within:ring-plum/20
                     transition-colors duration-150"
        >
          {/* Invisible native date input covers the full row — tap opens picker */}
          <input
            ref={inputRef}
            id={id}
            type="date"
            value={datePart}
            onChange={e => handleDateChange(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          <span className={`flex-1 text-small ${formatted ? 'text-midnight' : 'text-slate italic'}`}>
            {formatted ?? 'Set date'}
          </span>
          <Calendar size={16} className="text-slate shrink-0" />
        </div>
        {/* Time toggle button — only shown when a date is set */}
        {datePart && (
          <button
            type="button"
            onClick={handleTimeToggle}
            className={`h-11 w-11 shrink-0 rounded-lg border flex items-center justify-center transition-colors duration-150
              ${showTime
                ? 'border-plum bg-plum/10 text-plum'
                : 'border-stone bg-white text-slate hover:border-plum/40 hover:text-plum/60'
              }`}
            title={showTime ? 'Remove time' : 'Add time'}
          >
            <Clock size={16} />
          </button>
        )}
      </div>
      {/* Time input — shown when time is toggled on */}
      {showTime && (
        <div
          className="relative h-11 rounded-lg border border-stone bg-white flex items-center px-3 gap-2
                     focus-within:border-plum focus-within:ring-2 focus-within:ring-plum/20
                     transition-colors duration-150"
        >
          <input
            type="time"
            value={timePart}
            onChange={e => handleTimeChange(e.target.value)}
            className="flex-1 text-small text-midnight bg-transparent focus:outline-none"
          />
          <Clock size={16} className="text-slate shrink-0" />
        </div>
      )}
    </div>
  );
}

// ── Collapsible Section ───────────────────────────────────────────────────────

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-visible">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-4
                   hover:bg-mist active:bg-stone transition-colors duration-150
                   pointer-events-auto ${open ? 'rounded-t-xl' : 'rounded-xl'}`}
      >
        <span className="font-semibold text-small text-midnight">{title}</span>
        <ChevronDown
          size={18}
          className={`text-slate transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        // overflow-visible is required so absolutely-positioned popups inside
        // the card (Shift Type dropdown, AddressSearch suggestions, etc.) are
        // not clipped at the card's edges.
        <div className="px-4 pb-5 pt-1 flex flex-col gap-4 border-t border-stone overflow-visible">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Address sub-section ───────────────────────────────────────────────────────

function AddressSubSection({
  label,
  address,
  floor,
  officeNumber,
  onAddressChange,
  onFloorChange,
  onOfficeChange,
  addressId,
  hideAddress,
}: {
  label: string;
  address: SelectedPlace | null;
  floor: string;
  officeNumber: string;
  onAddressChange: (p: SelectedPlace | null) => void;
  onFloorChange: (v: string) => void;
  onOfficeChange: (v: string) => void;
  addressId: string;
  hideAddress?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate">{label}</p>
      {!hideAddress && (
        <AddressSearch
          id={addressId}
          value={address}
          onChange={onAddressChange}
          label="Address"
        />
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${addressId}-floor`} optional>Floor</FieldLabel>
          <TextInput
            id={`${addressId}-floor`}
            value={floor}
            onChange={onFloorChange}
            placeholder="e.g. 12"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${addressId}-office`} optional>Office #</FieldLabel>
          <TextInput
            id={`${addressId}-office`}
            value={officeNumber}
            onChange={onOfficeChange}
            placeholder="e.g. 1204"
          />
        </div>
      </div>
    </div>
  );
}

// ── Residential Property Card ─────────────────────────────────────────────────

function ResidentialPropertyCard({
  idPrefix,
  property,
  onChange,
}: {
  idPrefix: string;
  property: ResidentialPropertyDetails;
  onChange: (patch: Partial<ResidentialPropertyDetails>) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {/* Property Type */}
      <div className="flex flex-col gap-1.5">
        <FieldLabel htmlFor={`${idPrefix}-ptype`} optional>Property Type</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {PROPERTY_TYPES.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ propertyType: property.propertyType === opt.value ? undefined : opt.value })}
              className={`h-9 px-3 rounded-lg border text-[12px] font-semibold
                transition-all duration-150
                ${property.propertyType === opt.value
                  ? 'border-plum bg-plum/5 text-plum'
                  : 'border-stone bg-white text-midnight hover:border-plum/40'
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bedrooms + Bathrooms */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-bed`} optional>Bedrooms</FieldLabel>
          <input
            id={`${idPrefix}-bed`}
            type="number"
            min="0"
            step="1"
            value={property.bedrooms ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChange({ bedrooms: v === '' ? undefined : parseInt(v, 10) });
            }}
            placeholder="2"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-bath`} optional>Bathrooms</FieldLabel>
          <input
            id={`${idPrefix}-bath`}
            type="number"
            min="0"
            step="0.5"
            value={property.bathrooms ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChange({ bathrooms: v === '' ? undefined : parseFloat(v) });
            }}
            placeholder="1"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>
      </div>

      {/* Sq Ft + Stories */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-sqft`} optional>Square Footage</FieldLabel>
          <input
            id={`${idPrefix}-sqft`}
            type="number"
            min="0"
            step="1"
            value={property.squareFootage ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChange({ squareFootage: v === '' ? undefined : parseInt(v, 10) });
            }}
            placeholder="e.g. 1500"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-stories`} optional>Stories</FieldLabel>
          <input
            id={`${idPrefix}-stories`}
            type="number"
            min="0"
            step="1"
            value={property.stories ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChange({ stories: v === '' ? undefined : parseInt(v, 10) });
            }}
            placeholder="0"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>
      </div>

      {/* Garage Spaces + Total Room Count */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-garage`} optional>Garage Spaces</FieldLabel>
          <input
            id={`${idPrefix}-garage`}
            type="number"
            min="0"
            step="1"
            value={property.garageSpaces ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChange({ garageSpaces: v === '' ? undefined : parseInt(v, 10) });
            }}
            placeholder="0"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-rooms`} optional>Total Rooms</FieldLabel>
          <input
            id={`${idPrefix}-rooms`}
            type="number"
            min="0"
            step="1"
            value={property.totalRoomCount ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChange({ totalRoomCount: v === '' ? undefined : parseInt(v, 10) });
            }}
            placeholder="0"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>
      </div>

      {/* Basement + Elevator + Pool toggles */}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => onChange({ basement: !property.basement })}
          className="flex items-center gap-2"
        >
          {property.basement
            ? <ToggleRight size={26} className="text-plum" />
            : <ToggleLeft size={26} className="text-slate" />
          }
          <span className="text-small font-medium text-midnight">Basement</span>
        </button>
        <button
          type="button"
          onClick={() => onChange({ elevator: !property.elevator })}
          className="flex items-center gap-2"
        >
          {property.elevator
            ? <ToggleRight size={26} className="text-plum" />
            : <ToggleLeft size={26} className="text-slate" />
          }
          <span className="text-small font-medium text-midnight">Elevator</span>
        </button>
        <button
          type="button"
          onClick={() => onChange({ pool: !property.pool })}
          className="flex items-center gap-2"
        >
          {property.pool
            ? <ToggleRight size={26} className="text-plum" />
            : <ToggleLeft size={26} className="text-slate" />
          }
          <span className="text-small font-medium text-midnight">Pool</span>
        </button>
        <button
          type="button"
          onClick={() => onChange({ longCarry: !property.longCarry })}
          className="flex items-center gap-2"
        >
          {property.longCarry
            ? <ToggleRight size={26} className="text-plum" />
            : <ToggleLeft size={26} className="text-slate" />
          }
          <span className="text-small font-medium text-midnight">Long Carry</span>
        </button>
      </div>
    </div>
  );
}

// ── RentCast property type mapping ────────────────────────────────────────────

const RENTCAST_PROPERTY_TYPE_MAP: Record<string, ResidentialPropertyType> = {
  'Single Family': 'single_family',
  'Townhouse': 'townhouse',
  'Condo': 'condo',
  'Condominium': 'condo',
  'Apartment': 'apartment',
  'Manufactured': 'manufactured',
  'Mobile Home': 'manufactured',
  'Multi-Family': 'multi_family',
};

function mapRentCastToProperty(data: RentCastPropertyData): Partial<ResidentialPropertyDetails> {
  const patch: Partial<ResidentialPropertyDetails> = {};
  if (data.propertyType) {
    const mapped = RENTCAST_PROPERTY_TYPE_MAP[data.propertyType];
    if (mapped) patch.propertyType = mapped;
  }
  if (data.bedrooms != null) patch.bedrooms = data.bedrooms;
  if (data.bathrooms != null) patch.bathrooms = data.bathrooms;
  if (data.squareFootage != null) patch.squareFootage = data.squareFootage;

  // Map from RentCast features object
  const f = data.features as Record<string, unknown> | undefined;
  if (f) {
    // Stories from floorCount
    if (typeof f.floorCount === 'number' && f.floorCount > 0) {
      patch.stories = f.floorCount;
    }
    // Garage spaces
    if (typeof f.garageSpaces === 'number') {
      patch.garageSpaces = f.garageSpaces;
    } else if (typeof f.garageType === 'string' && f.garageType.toLowerCase() !== 'none') {
      // Garage exists but no spaces count — default to 1
      patch.garageSpaces = typeof f.garage === 'boolean' && f.garage ? 1 : (f.garageType ? 1 : 0);
    } else if (typeof f.garage === 'boolean') {
      patch.garageSpaces = f.garage ? 1 : 0;
    }
    // Total room count
    if (typeof f.roomCount === 'number') {
      patch.totalRoomCount = f.roomCount;
    }
    // Pool
    if (typeof f.pool === 'boolean') {
      patch.pool = f.pool;
    }
  }

  return patch;
}

// ── Property Details Modal ───────────────────────────────────────────────────

function formatFeatureValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') return value || '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  return JSON.stringify(value);
}

function formatFeatureKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

function PropertyDetailsModal({
  data,
  error,
  onClose,
}: {
  data: RentCastPropertyData | null;
  error?: string | null;
  onClose: () => void;
}) {
  const rows: { label: string; value: string }[] = data ? [
    { label: 'Address', value: data.formattedAddress ?? '—' },
    { label: 'Property Type', value: data.propertyType ?? '—' },
    { label: 'Bedrooms', value: data.bedrooms != null ? String(data.bedrooms) : '—' },
    { label: 'Bathrooms', value: data.bathrooms != null ? String(data.bathrooms) : '—' },
    { label: 'Square Footage', value: data.squareFootage != null ? data.squareFootage.toLocaleString() + ' sq ft' : '—' },
    { label: 'Lot Size', value: data.lotSize != null ? data.lotSize.toLocaleString() + ' sq ft' : '—' },
    { label: 'Year Built', value: data.yearBuilt != null ? String(data.yearBuilt) : '—' },
    { label: 'County', value: data.county ?? '—' },
    { label: 'Owner(s)', value: data.ownerNames?.join(', ') || '—' },
    { label: 'Legal Description', value: data.legalDescription ?? '—' },
  ] : [];

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/40" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col
                      md:rounded-2xl md:max-w-md md:w-full md:max-h-[70vh]">
        <div className="flex items-center justify-between px-4 py-4 border-b border-stone shrink-0">
          <p className="font-semibold text-small text-midnight">Imported Property Details</p>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:text-midnight active:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-4 flex flex-col gap-3">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {!error && rows.map(r => (
            <div key={r.label}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate">{r.label}</p>
              <p className="text-small text-midnight mt-0.5">{r.value}</p>
            </div>
          ))}

          {/* Features */}
          {!error && data?.features && Object.keys(data.features).length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate mb-2">Features</p>
              <div className="bg-mist rounded-xl px-3 py-2 flex flex-col gap-1.5">
                {Object.entries(data.features).map(([key, val]) => (
                  <div key={key} className="flex justify-between gap-3">
                    <span className="text-[12px] text-slate shrink-0">{formatFeatureKey(key)}</span>
                    <span className="text-[12px] text-midnight text-right">{formatFeatureValue(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!error && (
            <p className="text-[11px] text-slate text-center mt-2">
              Data provided by RentCast
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Transit counters ──────────────────────────────────────────────────────────

function TransitCounter({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | null;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex-1 bg-mist rounded-xl px-4 py-3 flex flex-col items-center gap-1.5 text-center">
      <div className="text-plum">{icon}</div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate">{label}</p>
      <p className={`font-display text-[22px] font-bold leading-none ${value ? 'text-midnight' : 'text-stone'}`}>
        {value ?? '—'}
      </p>
    </div>
  );
}

// ── Logistics Flowchart components ────────────────────────────────────────────

function FlowNode({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 shrink-0 rounded-xl bg-mist flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function FlowConnector() {
  return (
    <div className="flex gap-3 py-0.5">
      <div className="w-9 shrink-0 flex justify-center">
        <ArrowDown size={13} className="text-stone" />
      </div>
    </div>
  );
}

function WaypointIcon({ type }: { type: WaypointType }) {
  if (type === 'Stairs') return <ChevronsUp size={16} className="text-slate" />;
  if (type === 'Elevator') return <ArrowUpDown size={16} className="text-slate" />;
  return <Truck size={16} className="text-slate" />;
}

function StridesInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const n = parseInt(value, 10);
  const feet = !isNaN(n) && n > 0 ? n * 3 : null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label htmlFor={id} className="text-small font-medium text-midnight">Strides</label>
      <input
        id={id}
        type="number"
        min="1"
        step="1"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-16 h-9 text-center rounded-lg border border-stone bg-white
                   text-small font-semibold text-midnight
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors"
        placeholder="0"
      />
      {feet !== null && <span className="text-[12px] text-slate">({feet} ft)</span>}
    </div>
  );
}

function WaypointSelector({
  value,
  onChange,
  truckLabel = 'Truck',
}: {
  value: WaypointType;
  onChange: (w: WaypointType) => void;
  truckLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Via</p>
      <div className="flex gap-1.5">
        {WAYPOINTS.map(w => (
          <button
            key={w}
            type="button"
            onClick={() => onChange(w)}
            className={`flex-1 h-9 rounded-lg border text-[12px] font-semibold
              transition-all duration-150
              ${value === w
                ? 'border-plum bg-plum/5 text-plum'
                : 'border-stone bg-white text-midnight hover:border-plum/40'
              }`}
          >
            {w === 'Truck' ? truckLabel : w}
          </button>
        ))}
      </div>
    </div>
  );
}

function LoadingDockToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className="shrink-0 mt-0.5"
        aria-label="Toggle truck-height loading dock"
      >
        {value
          ? <ToggleRight size={26} className="text-plum" />
          : <ToggleLeft size={26} className="text-slate" />
        }
      </button>
      <div>
        <p className="text-small font-medium text-midnight">Truck-height loading dock</p>
        <p className="text-[12px] text-slate leading-snug mt-0.5">Available at this location</p>
      </div>
    </div>
  );
}

function LogisticsFlowChart({
  direction,
  isInternalMove,
  value,
  onChange,
  idPrefix,
}: {
  direction: 'origin' | 'destination';
  isInternalMove: boolean;
  value: LogisticsFormState;
  onChange: (patch: Partial<LogisticsFormState>) => void;
  idPrefix: string;
}) {
  const noIntermediateStop = value.waypoint === 'Truck';

  const waypointNode = (
    <FlowNode icon={<WaypointIcon type={value.waypoint} />}>
      <div className="flex flex-col gap-2">
        <WaypointSelector
          value={value.waypoint}
          truckLabel={direction === 'destination' ? 'None' : (isInternalMove ? 'Same floor' : 'Truck')}
          onChange={w => onChange({
            waypoint: w,
            // Stairs: keep existing strides2, default stories to '1' if unset
            // Elevator / Truck / None: clear strides2 and stories
            strides2: w === 'Stairs' ? value.strides2 : '',
            stories: w === 'Stairs' ? (value.stories || '1') : '',
          })}
        />
        {value.waypoint === 'Stairs' && (
          <div className="flex items-center gap-2">
            <Layers size={14} className="text-slate shrink-0" />
            <label
              htmlFor={`${idPrefix}-stories`}
              className="text-small font-medium text-midnight whitespace-nowrap"
            >
              Stories
            </label>
            <input
              id={`${idPrefix}-stories`}
              type="number"
              min="1"
              step="1"
              value={value.stories}
              onChange={e => onChange({ stories: e.target.value })}
              className="w-14 h-9 text-center rounded-lg border border-stone bg-white
                         text-small font-semibold text-midnight
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors"
              placeholder="0"
            />
          </div>
        )}
      </div>
    </FlowNode>
  );

  const strides2Row = (
    <>
      <FlowConnector />
      <FlowNode icon={<Footprints size={16} className="text-slate" />}>
        <StridesInput
          id={`${idPrefix}-s2`}
          value={value.strides2}
          onChange={v => onChange({ strides2: v })}
        />
      </FlowNode>
      <FlowConnector />
    </>
  );

  if (direction === 'origin') {
    return (
      <div className="flex flex-col gap-0">
        <FlowNode icon={<Armchair size={16} className="text-slate" />}>
          <p className="text-small font-medium text-midnight">Mid-point on origin floor</p>
        </FlowNode>
        <FlowConnector />
        <FlowNode icon={<Footprints size={16} className="text-slate" />}>
          <StridesInput
            id={`${idPrefix}-s1`}
            value={value.strides1}
            onChange={v => onChange({ strides1: v })}
          />
        </FlowNode>
        <FlowConnector />
        {waypointNode}
        {!noIntermediateStop ? strides2Row : <FlowConnector />}
        <FlowNode
          icon={
            isInternalMove
              ? <Armchair size={16} className="text-slate" />
              : <Truck size={16} className="text-slate" />
          }
        >
          <p className="text-small font-medium text-midnight">
            {isInternalMove ? 'Same floor' : 'Truck'}
          </p>
        </FlowNode>
        {!isInternalMove && (
          <div className="mt-4 pl-12">
            <LoadingDockToggle
              value={value.loadingDock}
              onChange={v => onChange({ loadingDock: v })}
            />
          </div>
        )}
      </div>
    );
  }

  // destination — inverted: truck → desk
  return (
    <div className="flex flex-col gap-0">
      <div className="mb-4 pl-12">
        <LoadingDockToggle
          value={value.loadingDock}
          onChange={v => onChange({ loadingDock: v })}
        />
      </div>
      <FlowNode icon={<Truck size={16} className="text-slate" />}>
        <p className="text-small font-medium text-midnight">Truck</p>
      </FlowNode>
      <FlowConnector />
      <FlowNode icon={<Footprints size={16} className="text-slate" />}>
        <StridesInput
          id={`${idPrefix}-s1`}
          value={value.strides1}
          onChange={v => onChange({ strides1: v })}
        />
      </FlowNode>
      <FlowConnector />
      {waypointNode}
      {!noIntermediateStop ? strides2Row : <FlowConnector />}
      <FlowNode icon={<Armchair size={16} className="text-slate" />}>
        <p className="text-small font-medium text-midnight">Mid-point on destination floor</p>
      </FlowNode>
    </div>
  );
}

// ── Warehouse Drawer ──────────────────────────────────────────────────────────

function WarehouseDrawer({
  direction,
  warehouses,
  warehouseTransit,
  selectedId,
  onSelect,
  onClose,
}: {
  direction: 'origin' | 'destination';
  warehouses: WarehouseType[];
  warehouseTransit: Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>;
  selectedId: string | null;
  onSelect: (id: string, travelSeconds: number) => void;
  onClose: () => void;
}) {
  const getTravelSecs = (wId: string) => {
    const leg = direction === 'origin'
      ? warehouseTransit[wId]?.toOrigin
      : warehouseTransit[wId]?.toDestination;
    return leg?.durationSeconds;
  };
  const dirLabel = direction === 'origin' ? 'to origin' : 'to destination';

  return (
    /* Mobile: bottom sheet. md+: centered dialog. */
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/40" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col
                      md:rounded-2xl md:max-w-sm md:w-full md:max-h-[70vh]">
        <div className="flex items-center justify-between px-4 py-4 border-b border-stone shrink-0">
          <p className="font-semibold text-small text-midnight">
            {direction === 'origin' ? 'Origin Warehouse' : 'Destination Warehouse'}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:text-midnight active:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>
        <div className="overflow-y-auto">
          {warehouses.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-small text-slate">No warehouses configured yet.</p>
              <p className="text-[12px] text-slate mt-1">Add warehouses in Admin → Warehouses.</p>
            </div>
          ) : (
            <ul className="divide-y divide-stone">
              {warehouses.map(w => {
                const travelSecs = getTravelSecs(w.id);
                const isSelected = selectedId === w.id;
                return (
                  <li key={w.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(w.id, travelSecs ?? 0)}
                      className={`w-full text-left px-4 py-4 flex items-start gap-3
                        transition-colors duration-100
                        ${isSelected ? 'bg-plum/5' : 'hover:bg-mist active:bg-stone'}`}
                    >
                      <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5
                        ${isSelected ? 'bg-plum/10' : 'bg-mist'}`}>
                        <Warehouse size={16} className={isSelected ? 'text-plum' : 'text-slate'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-small font-semibold truncate ${isSelected ? 'text-plum' : 'text-midnight'}`}>
                          {w.name}
                        </p>
                        <p className="text-[12px] text-slate mt-0.5 leading-snug">{w.address}</p>
                        {w.note && (
                          <p className="text-[11px] text-slate mt-0.5 italic leading-snug">{w.note}</p>
                        )}
                        {travelSecs !== undefined ? (
                          <p className="text-[12px] font-semibold text-plum mt-1">
                            {fmtSecs(travelSecs)} {dirLabel}
                          </p>
                        ) : (
                          <p className="text-[12px] text-stone mt-1">Travel time not calculated yet</p>
                        )}
                      </div>
                      {isSelected && (
                        <div className="shrink-0 w-5 h-5 rounded-full bg-plum flex items-center justify-center mt-1">
                          <div className="w-2 h-2 rounded-full bg-white" />
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function DatesLogisticsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMoveView = location.pathname.includes('/moves/');
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const orgUnits = 'imperial' as const;

  const {
    quote,
    loading,
    updateQuote,
    calculateTransit,
    calculateWarehouseTransits,
  } = useSharedQuote();
  const { org, loading: orgLoading } = useOrgSettings(orgId ?? '');
  const { warehouses } = useOrgWarehouses(orgId ?? '');

  const [initialized, setInitialized] = useState(false);
  const justInitialized = useRef(false);

  // Moving Date
  const [packingDate, setPackingDate] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [timing, setTiming] = useState('');
  const [accessorialServicesDate, setAccessorialServicesDate] = useState('');
  const accDateDirty = useRef(false);

  // Addresses — Origin
  const [originPlace, setOriginPlace] = useState<SelectedPlace | null>(null);
  const [originFloor, setOriginFloor] = useState('');
  const [originOffice, setOriginOffice] = useState('');
  const [originApt, setOriginApt] = useState('');

  // Internal move
  const [internalMove, setInternalMove] = useState(false);

  // Move type (auto-populated from addresses)
  const [moveType, setMoveType] = useState<'local' | 'intrastate' | 'interstate' | ''>('');

  // Addresses — Destination
  const [destPlace, setDestPlace] = useState<SelectedPlace | null>(null);
  const [destFloor, setDestFloor] = useState('');
  const [destOffice, setDestOffice] = useState('');
  const [destApt, setDestApt] = useState('');

  // Transit
  const [transit, setTransit] = useState<import('../../types/admin').QuoteTransit | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState('');

  // Warehouse transit
  const [warehouseTransit, setWarehouseTransit] = useState<
    Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>
  >({});
  const [calcingWarehouses, setCalcingWarehouses] = useState(false);

  // Travel to job warehouse selections
  const [originWarehouseId, setOriginWarehouseId] = useState<string | null>(null);
  const [destWarehouseId, setDestWarehouseId] = useState<string | null>(null);
  const [originTravelSecs, setOriginTravelSecs] = useState<number | null>(null);
  const [destTravelSecs, setDestTravelSecs] = useState<number | null>(null);
  const [warehouseDrawer, setWarehouseDrawer] = useState<'origin' | 'destination' | null>(null);

  // Logistics
  const [origLogistics, setOrigLogistics] = useState<LogisticsFormState>(DEFAULT_LOGISTICS);
  const [destLogistics, setDestLogistics] = useState<LogisticsFormState>(DEFAULT_LOGISTICS);

  // Residential property details
  const [originProperty, setOriginProperty] = useState<ResidentialPropertyDetails>({ ...RES_PROP_DEFAULTS });
  const [destProperty, setDestProperty] = useState<ResidentialPropertyDetails>({ ...RES_PROP_DEFAULTS });

  // RentCast imported property data
  const [originRentCast, setOriginRentCast] = useState<RentCastPropertyData | null>(null);
  const [destRentCast, setDestRentCast] = useState<RentCastPropertyData | null>(null);
  const [rentCastLoading, setRentCastLoading] = useState<'origin' | 'destination' | null>(null);
  const [rentCastModal, setRentCastModal] = useState<'origin' | 'destination' | null>(null);
  const [rentCastError, setRentCastError] = useState<{ direction: 'origin' | 'destination'; message: string } | null>(null);

  // Travel time rate (quote-level override; defaults to org setting)
  const [travelTimeRate, setTravelTimeRate] = useState<TravelTimeRate | ''>('');
  // Ref to ensure we only perform the one-time "stamp org default on quote" save once.
  const travelTimeRateSavedRef = useRef(false);

  // Save
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track last address pair for auto-transit to avoid re-triggering
  const lastAutoTransitKey = useRef('');

  // ── Initialize from quote ──────────────────────────────────────────────────

  useEffect(() => {
    if (quote && !loading && !initialized) {
      justInitialized.current = true;
      setPackingDate(quote.packingDate ?? '');
      setPreferredDate(quote.movingDate?.preferredDate ?? '');
      setDeadline(quote.movingDate?.deadline ?? '');
      setTiming(quote.movingDate?.shiftType ?? quote.movingDate?.timing ?? '');
      setAccessorialServicesDate(quote.accessorialServicesDate ?? '');

      const orig = quote.addresses?.origin;
      if (orig) {
        setOriginPlace({ address: orig.address, placeId: orig.placeId });
        setOriginFloor(orig.floor ?? '');
        setOriginOffice(orig.officeNumber ?? '');
        setOriginApt(orig.apt ?? '');
      }
      setInternalMove(quote.addresses?.internalMove ?? false);
      setMoveType(quote.moveType ?? '');

      const dest = quote.addresses?.destination;
      if (dest) {
        setDestPlace({ address: dest.address, placeId: dest.placeId });
        setDestFloor(dest.floor ?? '');
        setDestOffice(dest.officeNumber ?? '');
        setDestApt(dest.apt ?? '');
      }

      const savedTransit = quote.addresses?.transit ?? null;
      setTransit(savedTransit);
      // Mark as already calculated so we don't auto-re-trigger
      if (orig && dest && savedTransit) {
        lastAutoTransitKey.current = `${orig.placeId}|${dest.placeId}`;
      }

      setWarehouseTransit(quote.warehouseTransit ?? {});
      const ttj = quote.travelToJob;
      if (ttj?.originWarehouseId) setOriginWarehouseId(ttj.originWarehouseId);
      if (ttj?.destinationWarehouseId) setDestWarehouseId(ttj.destinationWarehouseId);
      if (ttj?.originTravelSeconds != null) setOriginTravelSecs(ttj.originTravelSeconds);
      if (ttj?.destinationTravelSeconds != null) setDestTravelSecs(ttj.destinationTravelSeconds);

      setOrigLogistics(fromLogisticsData(quote.originLogistics));
      setDestLogistics(fromLogisticsData(quote.destinationLogistics));
      setTravelTimeRate(quote.travelTimeRate ?? '');
      // Residential property details
      if (orig?.residentialProperty) setOriginProperty({ ...RES_PROP_DEFAULTS, ...orig.residentialProperty });
      if (dest?.residentialProperty) setDestProperty({ ...RES_PROP_DEFAULTS, ...dest.residentialProperty });
      // Restore imported RentCast data if previously saved
      if (orig?.importedPropertyData) setOriginRentCast(orig.importedPropertyData);
      if (dest?.importedPropertyData) setDestRentCast(dest.importedPropertyData);
      setInitialized(true);
    }
  }, [quote, loading, initialized]);

  // Org settings arrive after the quote on first render. Until they do, there is
  // no default to apply: resolveOrgTravelTimeRate() answers "1 Hr Standard" for a
  // missing org, which must never be filled in or persisted as the org default.
  const orgDefaultsReady = !orgLoading && !!org;

  // If org settings load after quote, fill the local rate from the org default.
  useEffect(() => {
    if (travelTimeRate || !orgDefaultsReady) return;
    const resolved = resolveOrgTravelTimeRate(org, quote?.type);
    if (!resolved) return;
    setTravelTimeRate(resolved);
  }, [travelTimeRate, org, orgDefaultsReady, quote?.type]);

  // Stamp the org default onto the quote once if it hasn't been saved there yet.
  useEffect(() => {
    if (travelTimeRateSavedRef.current || !quote || quote.quoteStatus !== 'draft') return;
    if (quote.travelTimeRate) { travelTimeRateSavedRef.current = true; return; }
    if (!orgDefaultsReady) return;
    const rate = resolveOrgTravelTimeRate(org, quote.type);
    if (!rate) return;
    travelTimeRateSavedRef.current = true;
    updateQuote({ travelTimeRate: rate }).catch(() => {});
  }, [quote, org, orgDefaultsReady, updateQuote]);

  // Apply the org's default shift when the quote has no shift selected yet.
  // Runs once per mount, after both quote and org settings have loaded. If the
  // org hasn't explicitly marked a default, fall back to the first shift.
  const shiftDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialized || shiftDefaultAppliedRef.current || !org) return;
    if (timing) { shiftDefaultAppliedRef.current = true; return; }
    const shifts: ShiftSetting[] = quote?.type === 'residential'
      ? (org.residentialShiftSettings ?? DEFAULT_SHIFT_SETTINGS)
      : (org.commercialShiftSettings ?? DEFAULT_SHIFT_SETTINGS);
    if (shifts.length === 0) return;
    const defaultShift = shifts.find(s => s.isDefault) ?? shifts[0];
    if (defaultShift?.name) {
      shiftDefaultAppliedRef.current = true;
      setTiming(defaultShift.name);
    }
  }, [initialized, org, quote?.type, timing]);

  // ── Warehouse transit helper ───────────────────────────────────────────────

  const triggerWarehouseTransits = useCallback(async (originPid: string, destPid: string) => {
    if (!warehouses.length) return;
    setCalcingWarehouses(true);
    try {
      const result = await calculateWarehouseTransits(
        warehouses.map(w => ({ id: w.id, placeId: w.placeId })),
        originPid,
        destPid,
      );
      setWarehouseTransit(result);

      // Auto-select warehouse with shortest travel time (always re-evaluate on address change)
      let bestOriginId: string | null = null;
      let bestOriginSecs = Infinity;
      let bestDestId: string | null = null;
      let bestDestSecs = Infinity;
      for (const [wId, data] of Object.entries(result)) {
        const toO = data.toOrigin?.durationSeconds ?? Infinity;
        const toD = data.toDestination?.durationSeconds ?? Infinity;
        if (toO < bestOriginSecs) { bestOriginSecs = toO; bestOriginId = wId; }
        if (toD < bestDestSecs) { bestDestSecs = toD; bestDestId = wId; }
      }
      if (bestOriginId && bestOriginSecs !== Infinity) {
        setOriginWarehouseId(bestOriginId);
        setOriginTravelSecs(bestOriginSecs);
      }
      if (bestDestId && bestDestSecs !== Infinity) {
        setDestWarehouseId(bestDestId);
        setDestTravelSecs(bestDestSecs);
      }
    } catch (err) {
      console.error('[warehouse transit]', err);
    } finally {
      setCalcingWarehouses(false);
    }
  }, [warehouses, calculateWarehouseTransits]);

  // ── Auto-calculate transit when addresses change ──────────────────────────

  useEffect(() => {
    if (!initialized || internalMove) return;
    const key = `${originPlace?.placeId ?? ''}|${destPlace?.placeId ?? ''}`;
    if (!originPlace?.placeId || !destPlace?.placeId) return;
    if (key === lastAutoTransitKey.current) return;
    lastAutoTransitKey.current = key;

    setCalculating(true);
    setCalcError('');
    calculateTransit(originPlace.placeId, destPlace.placeId)
      .then(result => {
        setTransit(result);
        setCalculating(false);
        // Fire warehouse transits in background (don't await)
        triggerWarehouseTransits(originPlace!.placeId, destPlace!.placeId);
      })
      .catch(err => {
        lastAutoTransitKey.current = ''; // Reset to allow retry
        setCalcError(err instanceof Error ? err.message : 'Could not calculate route.');
        setCalculating(false);
      });
  }, [originPlace?.placeId, destPlace?.placeId, initialized, internalMove, calculateTransit, triggerWarehouseTransits]);

  // ── Auto-populate move type when addresses or transit change ────────────────
  useEffect(() => {
    if (!initialized) return;
    if (internalMove) { setMoveType('local'); return; }
    if (!originPlace?.address || !destPlace?.address) return;
    const inferred = inferMoveType(originPlace.address, destPlace.address, transit, org);
    if (inferred) setMoveType(inferred);
  }, [originPlace?.address, destPlace?.address, internalMove, initialized, transit, org]);

  // ── Auto-save ─────────────────────────────────────────────────────────────

  // Resolved rate: local state wins; org default is the fallback for new quotes.
  // While org settings are still loading there is no fallback, so an auto-save
  // that fires in that window leaves travelTimeRate untouched on the quote.
  const effectiveTravelTimeRate = (travelTimeRate || (orgDefaultsReady ? resolveOrgTravelTimeRate(org, quote?.type) : undefined)) as TravelTimeRate | undefined;

  const isResidential = quote?.type === 'residential';

  const doAutoSave = useCallback(() => {
    if (quote?.quoteStatus !== 'draft') return;
    const travelToJob: TravelToJob | undefined =
      (originWarehouseId || destWarehouseId)
        ? {
            originWarehouseId: originWarehouseId ?? undefined,
            destinationWarehouseId: destWarehouseId ?? undefined,
            originTravelSeconds: originTravelSecs ?? undefined,
            destinationTravelSeconds: destTravelSecs ?? undefined,
          }
        : undefined;

    // For residential quotes, attach property details to addresses
    const origResProp = isResidential ? originProperty : undefined;
    const destResProp = isResidential ? destProperty : undefined;

    setSaving(true);
    setSaveError('');
    updateQuote({
      packingDate: packingDate || null,
      // Only include accessorialServicesDate if the user changed it on this page,
      // otherwise omit it so we don't overwrite values saved from AccessorialServicesPage
      ...(accDateDirty.current && !isResidential ? { accessorialServicesDate: accessorialServicesDate || null } : {}),
      moveType: moveType || undefined,
      movingDate: {
        preferredDate: preferredDate || undefined,
        deadline: isResidential ? undefined : (deadline || undefined),
        timing: isResidential ? undefined : ((timing as MoveTiming) || undefined),
        shiftType: timing || undefined,
        shiftLengthHours: (() => {
          if (!timing) return undefined;
          const quoteType = isResidential ? 'residential' : 'commercial';
          const shifts: ShiftSetting[] = quoteType === 'commercial'
            ? (org?.commercialShiftSettings ?? DEFAULT_SHIFT_SETTINGS)
            : (org?.residentialShiftSettings ?? DEFAULT_SHIFT_SETTINGS);
          const match = shifts.find(s => s.name === timing);
          return match?.lengthHours;
        })(),
      },
      addresses: {
        origin: buildAddress(originPlace, originFloor, originOffice, origResProp, originRentCast, originApt),
        internalMove,
        destination: internalMove
          ? buildAddress(originPlace, destFloor, destOffice, destResProp, destRentCast, destApt)
          : buildAddress(destPlace, destFloor, destOffice, destResProp, destRentCast, destApt),
        transit: transit ?? undefined,
      },
      originLogistics: isResidential ? undefined : toLogisticsData(origLogistics, !internalMove),
      destinationLogistics: isResidential ? undefined : (internalMove ? undefined : toLogisticsData(destLogistics, true)),
      travelToJob,
      travelTimeRate: effectiveTravelTimeRate,
    })
      .then(() => setSaving(false))
      .catch(err => {
        setSaveError(err instanceof Error ? err.message : 'Auto-save failed');
        setSaving(false);
      });
  }, [
    packingDate, preferredDate, deadline, timing, accessorialServicesDate, moveType,
    originPlace, originFloor, originOffice, originApt,
    internalMove, destPlace, destFloor, destOffice, destApt,
    transit, origLogistics, destLogistics,
    originWarehouseId, destWarehouseId, originTravelSecs, destTravelSecs,
    effectiveTravelTimeRate, isResidential, originProperty, destProperty, originRentCast, destRentCast,
    updateQuote, quote?.quoteStatus,
  ]);

  useEffect(() => {
    if (!initialized) return;
    if (justInitialized.current) { justInitialized.current = false; return; }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(doAutoSave, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [doAutoSave, initialized]);

  // ── Manual refresh ────────────────────────────────────────────────────────

  async function handleManualRefresh() {
    if (!originPlace || (!internalMove && !destPlace)) return;
    if (internalMove) {
      setTransit({ distanceMeters: 0, durationSeconds: 0, calculatedAt: new Date().toISOString() });
      return;
    }
    setCalculating(true);
    setCalcError('');
    lastAutoTransitKey.current = `${originPlace.placeId}|${destPlace!.placeId}`;
    try {
      const result = await calculateTransit(originPlace.placeId, destPlace!.placeId);
      setTransit(result);
      setCalculating(false);
      triggerWarehouseTransits(originPlace.placeId, destPlace!.placeId);
    } catch (err) {
      lastAutoTransitKey.current = '';
      setCalcError(err instanceof Error ? err.message : 'Could not calculate route.');
      setCalculating(false);
    }
  }

  // ── RentCast property lookup ────────────────────────────────────────────

  async function handleRentCastImport(direction: 'origin' | 'destination') {
    const address = direction === 'origin' ? originPlace?.address : (internalMove ? originPlace?.address : destPlace?.address);
    if (!address || !orgId) return;

    setRentCastLoading(direction);
    setRentCastError(null);
    try {
      const res = await api.get<{ success: boolean; data: RentCastPropertyData | null }>(
        `/quotes/${orgId}/property-lookup?address=${encodeURIComponent(address)}`,
      );
      const data = res.data;
      if (!data) {
        setRentCastLoading(null);
        return;
      }

      const patch = mapRentCastToProperty(data);
      if (direction === 'origin') {
        setOriginRentCast(data);
        // Manual click: overwrite existing values
        setOriginProperty(prev => ({ ...prev, ...patch }));
      } else {
        setDestRentCast(data);
        setDestProperty(prev => ({ ...prev, ...patch }));
      }
    } catch (err) {
      console.error('[RentCast]', err);
      const message = err instanceof Error ? err.message : 'Could not fetch property details from RentCast.';
      // If existing imported data was for a different address, clear it
      const currentAddress = direction === 'origin' ? originPlace?.address : (internalMove ? originPlace?.address : destPlace?.address);
      if (direction === 'origin') {
        if (originRentCast && originRentCast.formattedAddress !== currentAddress) {
          setOriginRentCast(null);
        }
      } else {
        if (destRentCast && destRentCast.formattedAddress !== currentAddress) {
          setDestRentCast(null);
        }
      }
      setRentCastError({ direction, message });
      setRentCastModal(direction);
    } finally {
      setRentCastLoading(null);
    }
  }

  // ── Warehouse selection ───────────────────────────────────────────────────

  function selectWarehouse(dir: 'origin' | 'destination', warehouseId: string, travelSeconds: number) {
    if (dir === 'origin') {
      setOriginWarehouseId(warehouseId);
      setOriginTravelSecs(travelSeconds);
    } else {
      setDestWarehouseId(warehouseId);
      setDestTravelSecs(travelSeconds);
    }
    setWarehouseDrawer(null);
  }

  const isPortalToPortal = effectiveTravelTimeRate === 'portal_to_portal';
  const isOneHourStandard = effectiveTravelTimeRate === 'one_hour_standard';

  function getWarehouseName(id: string | null): string {
    if (!id) return 'Select warehouse';
    return warehouses.find(w => w.id === id)?.name ?? 'Unknown';
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const canRefresh = !!originPlace && (internalMove || !!destPlace);

  // On md+, expand all collapsible sections by default
  const desktopExpanded = typeof window !== 'undefined'
    && (window.matchMedia?.('(min-width: 768px)').matches ?? false);

  return (
    <div className="px-4 pt-6 pb-10">
      {/* Back — hidden on md+ (shell left panel provides navigation) */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">{isMoveView ? 'Dates & Addresses' : 'Dates & Logistics'}</h1>
        {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
      </div>

      {quote.quoteStatus !== 'draft' && <ReadOnlyBanner status={quote.quoteStatus} />}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      <div className={`${quote.quoteStatus !== 'draft' ? 'pointer-events-none opacity-60 select-none' : ''}`}>

      {isResidential ? (
        /* ── Residential layout: Key Dates, Addresses, Origin Property, Dest Property ── */
        <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4 md:items-start">

          {/* ── Left column: Key Dates + Addresses ── */}
          <div className="flex flex-col gap-3">

          {/* ── Key Dates Card ── */}
          <CollapsibleSection title="Key Dates" defaultOpen>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="dl-packing" optional>Packing Date</FieldLabel>
                <DateTimePicker value={packingDate} onChange={setPackingDate} placeholder="Set packing date" />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="dl-preferred" optional>Moving Date</FieldLabel>
                <DateTimePicker value={preferredDate} onChange={setPreferredDate} placeholder="Set moving date" />
              </div>
            </div>
          </CollapsibleSection>

          {/* ── Addresses Card ── */}
          <CollapsibleSection title="Addresses" defaultOpen>
            {/* Origin Address */}
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Origin</p>
            <AddressSearch
              id="dl-origin"
              value={originPlace}
              onChange={place => {
                setOriginPlace(place);
                setTransit(null);
                lastAutoTransitKey.current = '';
              }}
              label="Address"
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="dl-orig-floor" optional>Floor</FieldLabel>
                <TextInput
                  id="dl-orig-floor"
                  value={originFloor}
                  onChange={setOriginFloor}
                  placeholder="e.g. 12"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="dl-orig-apt" optional>Apt</FieldLabel>
                <TextInput
                  id="dl-orig-apt"
                  value={originApt}
                  onChange={setOriginApt}
                  placeholder="e.g. 4B"
                />
              </div>
            </div>

            {/* Destination Address */}
            <div className="border-t border-stone pt-4 mt-1" />
            <div className="flex items-start gap-3 py-1">
              <button
                type="button"
                onClick={() => { setInternalMove(v => !v); setTransit(null); lastAutoTransitKey.current = ''; }}
                className="shrink-0 mt-0.5"
                aria-label="Toggle internal move"
              >
                {internalMove
                  ? <ToggleRight size={28} className="text-plum" />
                  : <ToggleLeft size={28} className="text-slate" />
                }
              </button>
              <div>
                <p className="text-small font-semibold text-midnight">Internal Move</p>
                <p className="text-[12px] text-slate leading-snug mt-0.5">Move happens within the same building.</p>
              </div>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Destination</p>
            {!internalMove && (
              <AddressSearch
                id="dl-dest"
                value={destPlace}
                onChange={place => {
                  setDestPlace(place);
                  setTransit(null);
                  lastAutoTransitKey.current = '';
                }}
                label="Address"
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="dl-dest-floor" optional>Floor</FieldLabel>
                <TextInput
                  id="dl-dest-floor"
                  value={destFloor}
                  onChange={setDestFloor}
                  placeholder="e.g. 12"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="dl-dest-apt" optional>Apt</FieldLabel>
                <TextInput
                  id="dl-dest-apt"
                  value={destApt}
                  onChange={setDestApt}
                  placeholder="e.g. 4B"
                />
              </div>
            </div>

            {/* Transit Distance & Time */}
            <div className="border-t border-stone pt-4 mt-1" />
            {!internalMove ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate flex-1">Transit Distance & Time</p>
                  {(calculating || calcingWarehouses) && (
                    <LoaderCircle size={13} className="text-plum animate-spin shrink-0" />
                  )}
                  {canRefresh && !calculating && (
                    <button
                      type="button"
                      onClick={handleManualRefresh}
                      className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg
                                 text-slate hover:text-plum hover:bg-plum/5 active:bg-mist
                                 transition-colors duration-150"
                      title="Recalculate transit"
                      aria-label="Recalculate transit"
                    >
                      <RefreshCw size={13} />
                    </button>
                  )}
                </div>

                <div className="flex gap-3">
                  <TransitCounter
                    label="Distance"
                    value={transit ? formatDistance(transit.distanceMeters, orgUnits) : null}
                    icon={<Navigation size={18} />}
                  />
                  <TransitCounter
                    label="Est. Time"
                    value={transit ? formatDuration(transit.durationSeconds) : null}
                    icon={<Clock size={18} />}
                  />
                </div>

                {calcError && (
                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose/10 border border-rose/30">
                    <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
                    <p className="text-[12px] text-rose">{calcError}</p>
                  </div>
                )}

                {!canRefresh && (
                  <p className="text-[12px] text-slate leading-snug">
                    <MapPin size={12} className="inline mr-1 -mt-0.5" />
                    Set both addresses to calculate transit.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex gap-3">
                <TransitCounter label="Distance" value="Same building" icon={<Navigation size={18} />} />
                <TransitCounter label="Est. Time" value="N/A" icon={<Clock size={18} />} />
              </div>
            )}

            {/* Move Type */}
            {(originPlace || internalMove) && (
              <div className="flex flex-col gap-1.5 pt-1">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Move Type</p>
                <div className="flex gap-1.5">
                  {MOVE_TYPES.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setMoveType(opt.value); }}
                      className={`flex-1 h-9 rounded-lg border text-[13px] font-medium transition-all duration-150
                        ${moveType === opt.value
                          ? 'border-plum bg-plum/5 text-plum font-semibold'
                          : 'border-stone bg-white text-midnight hover:border-plum/40'
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Travel Time to Job — hidden for flat rate residential quotes */}
            {!(quote.type === 'residential' && org?.residentialPricingModel === 'flat_rate' && !quote.pricingOverride) && <div className="flex flex-col gap-2 pt-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
                Travel Time to Job
              </p>

              <div className="flex gap-1.5">
                {(['one_hour_standard', 'portal_to_portal'] as TravelTimeRate[]).map(rate => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => setTravelTimeRate(rate)}
                    className={`flex-1 h-9 rounded-lg border text-[12px] font-semibold
                      transition-all duration-150
                      ${effectiveTravelTimeRate === rate
                        ? 'border-plum bg-plum/5 text-plum'
                        : 'border-stone bg-white text-midnight hover:border-plum/40'
                      }`}
                  >
                    {rate === 'one_hour_standard' ? '1 Hr Standard' : 'Portal to Portal'}
                  </button>
                ))}
              </div>

              {isOneHourStandard && (
                <div className="bg-mist rounded-xl px-4 py-3 flex items-center gap-3">
                  <Clock size={16} className="text-plum shrink-0" />
                  <p className="text-small font-semibold text-midnight">1 hour (standard)</p>
                </div>
              )}

              {isPortalToPortal && (
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => setWarehouseDrawer('origin')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-stone bg-white
                               hover:border-plum/40 active:bg-mist transition-colors duration-150 text-left"
                  >
                    <Warehouse size={16} className="shrink-0 text-plum" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate">Origin Warehouse</p>
                      <p className="text-small font-semibold text-midnight mt-0.5 truncate">
                        {getWarehouseName(originWarehouseId)}
                      </p>
                      {originTravelSecs !== null && (
                        <p className="text-[12px] text-plum mt-0.5">
                          {fmtSecs(originTravelSecs)} to origin
                        </p>
                      )}
                    </div>
                    <ChevronRight size={16} className="shrink-0 text-slate" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setWarehouseDrawer('destination')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-stone bg-white
                               hover:border-plum/40 active:bg-mist transition-colors duration-150 text-left"
                  >
                    <Warehouse size={16} className="shrink-0 text-plum" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate">Destination Warehouse</p>
                      <p className="text-small font-semibold text-midnight mt-0.5 truncate">
                        {getWarehouseName(destWarehouseId)}
                      </p>
                      {destTravelSecs !== null && (
                        <p className="text-[12px] text-plum mt-0.5">
                          {fmtSecs(destTravelSecs)} to destination
                        </p>
                      )}
                    </div>
                    <ChevronRight size={16} className="shrink-0 text-slate" />
                  </button>
                </div>
              )}
            </div>}
          </CollapsibleSection>

          </div>{/* end left column */}

          {/* ── Right column: Origin Property Details + Destination Property Details ── */}
          {/* In the unified move view, property details have their own dedicated page
              (Access & Property Details), so we skip them here to avoid duplication. */}
          {!isMoveView && (
          <div className="flex flex-col gap-3">

          {/* ── Origin Property Details ── */}
          <CollapsibleSection title="Origin Property Details" defaultOpen>
            <div className="flex items-center gap-2">
              {originPlace && (
                <button
                  type="button"
                  onClick={() => handleRentCastImport('origin')}
                  disabled={rentCastLoading === 'origin'}
                  className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg
                             text-slate hover:text-plum hover:bg-plum/5 active:bg-mist
                             transition-colors duration-150 disabled:opacity-50"
                  title="Import property details from RentCast"
                  aria-label="Import origin property details"
                >
                  {rentCastLoading === 'origin'
                    ? <LoaderCircle size={14} className="animate-spin" />
                    : <RefreshCw size={14} />
                  }
                </button>
              )}
              {originRentCast && (
                <button
                  type="button"
                  onClick={() => setRentCastModal('origin')}
                  className="text-[12px] text-plum hover:underline text-left"
                >
                  View Imported Property Details
                </button>
              )}
            </div>
            <ResidentialPropertyCard
              idPrefix="dl-orig-prop"
              property={originProperty}
              onChange={patch => setOriginProperty(prev => ({ ...prev, ...patch }))}
            />
          </CollapsibleSection>

          {/* ── Destination Property Details ── */}
          <CollapsibleSection title="Destination Property Details" defaultOpen>
            <div className="flex items-center gap-2">
              {(internalMove ? originPlace : destPlace) && (
                <button
                  type="button"
                  onClick={() => handleRentCastImport('destination')}
                  disabled={rentCastLoading === 'destination'}
                  className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg
                             text-slate hover:text-plum hover:bg-plum/5 active:bg-mist
                             transition-colors duration-150 disabled:opacity-50"
                  title="Import property details from RentCast"
                  aria-label="Import destination property details"
                >
                  {rentCastLoading === 'destination'
                    ? <LoaderCircle size={14} className="animate-spin" />
                    : <RefreshCw size={14} />
                  }
                </button>
              )}
              {destRentCast && (
                <button
                  type="button"
                  onClick={() => setRentCastModal('destination')}
                  className="text-[12px] text-plum hover:underline text-left"
                >
                  View Imported Property Details
                </button>
              )}
            </div>
            <ResidentialPropertyCard
              idPrefix="dl-dest-prop"
              property={destProperty}
              onChange={patch => setDestProperty(prev => ({ ...prev, ...patch }))}
            />
          </CollapsibleSection>

          </div>
          )}{/* end right column */}

        </div>
      ) : (
        /* ── Commercial layout: dates + addresses (+ logistics when not in move view) ── */
        <div className={`flex flex-col gap-3 ${!isMoveView ? 'md:grid md:grid-cols-2 md:gap-4 md:items-start' : ''}`}>

        {/* ── Left column: Moving Date + Addresses ── */}
        <div className="flex flex-col gap-3">

        {/* ── Moving Date ── */}
        <CollapsibleSection title="Moving Date" defaultOpen>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="dl-packing" optional>Packing Date</FieldLabel>
              <DateTimePicker value={packingDate} onChange={setPackingDate} placeholder="Set packing date" />
            </div>
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="dl-preferred" optional>Preferred Moving Date</FieldLabel>
              <DateTimePicker
                value={preferredDate}
                onChange={(v) => {
                  setPreferredDate(v);
                  if (v && accessorialServicesDate) {
                    const moveDate = v.split('T')[0];
                    const accDate = accessorialServicesDate.split('T')[0];
                    if (accDate > moveDate) { accDateDirty.current = true; setAccessorialServicesDate(''); }
                  }
                }}
                placeholder="Set moving date"
              />
            </div>
            <DateInput id="dl-deadline" label="Moving Deadline" value={deadline} onChange={setDeadline} optional />
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="dl-accessorial" optional>Accessorial Services Date</FieldLabel>
              <DateTimePicker
                value={accessorialServicesDate}
                onChange={(v) => {
                  if (v && preferredDate) {
                    const accDate = v.split('T')[0];
                    const moveDate = preferredDate.split('T')[0];
                    if (accDate > moveDate) return;
                  }
                  accDateDirty.current = true;
                  setAccessorialServicesDate(v);
                }}
                maxDate={preferredDate ? preferredDate.split('T')[0] : undefined}
                placeholder="Set accessorial services date"
              />
            </div>
          </div>
          {accessorialServicesDate && preferredDate && accessorialServicesDate.split('T')[0] > preferredDate.split('T')[0] && (
            <p className="text-[12px] text-amber-600 -mt-1">
              Accessorial services date cannot be after the move date.
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="dl-timing" optional>Shift Type</FieldLabel>
            <CustomDropdown
              id="dl-timing"
              options={[
                { value: '', label: 'Select shift type' },
                ...((quote?.type === 'residential'
                  ? (org?.residentialShiftSettings ?? DEFAULT_SHIFT_SETTINGS)
                  : (org?.commercialShiftSettings ?? DEFAULT_SHIFT_SETTINGS)
                ).map((s: ShiftSetting) => ({
                  value: s.name,
                  label: `${s.name} (${s.lengthHours}h)`,
                }))),
              ]}
              value={timing}
              onChange={setTiming}
              openUpward
            />
          </div>
        </CollapsibleSection>

        {/* ── Addresses ── */}
        <CollapsibleSection title="Addresses" defaultOpen={desktopExpanded}>
          <AddressSubSection
            label="Origin"
            addressId="dl-origin"
            address={originPlace}
            floor={originFloor}
            officeNumber={originOffice}
            onAddressChange={place => {
              setOriginPlace(place);
              setTransit(null);
              lastAutoTransitKey.current = '';
            }}
            onFloorChange={setOriginFloor}
            onOfficeChange={setOriginOffice}
          />

          <div className="flex items-start gap-3 py-1">
            <button
              type="button"
              onClick={() => { setInternalMove(v => !v); setTransit(null); lastAutoTransitKey.current = ''; }}
              className="shrink-0 mt-0.5"
              aria-label="Toggle internal move"
            >
              {internalMove
                ? <ToggleRight size={28} className="text-plum" />
                : <ToggleLeft size={28} className="text-slate" />
              }
            </button>
            <div>
              <p className="text-small font-semibold text-midnight">Internal Move</p>
              <p className="text-[12px] text-slate leading-snug mt-0.5">Move happens within the same building.</p>
            </div>
          </div>

          <AddressSubSection
            label="Destination"
            addressId="dl-dest"
            address={internalMove ? originPlace : destPlace}
            floor={destFloor}
            officeNumber={destOffice}
            onAddressChange={place => {
              if (!internalMove) {
                setDestPlace(place);
                setTransit(null);
                lastAutoTransitKey.current = '';
              }
            }}
            onFloorChange={setDestFloor}
            onOfficeChange={setDestOffice}
            hideAddress={internalMove}
          />

          {/* ── Transit ── */}
          {!internalMove && (
            <div className="flex flex-col gap-3 pt-1">
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate flex-1">Transit</p>
                {(calculating || calcingWarehouses) && (
                  <LoaderCircle size={13} className="text-plum animate-spin shrink-0" />
                )}
                {canRefresh && !calculating && (
                  <button
                    type="button"
                    onClick={handleManualRefresh}
                    className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg
                               text-slate hover:text-plum hover:bg-plum/5 active:bg-mist
                               transition-colors duration-150"
                    title="Recalculate transit"
                    aria-label="Recalculate transit"
                  >
                    <RefreshCw size={13} />
                  </button>
                )}
              </div>

              <div className="flex gap-3">
                <TransitCounter
                  label="Distance"
                  value={transit ? formatDistance(transit.distanceMeters, orgUnits) : null}
                  icon={<Navigation size={18} />}
                />
                <TransitCounter
                  label="Est. Time"
                  value={transit ? formatDuration(transit.durationSeconds) : null}
                  icon={<Clock size={18} />}
                />
              </div>

              {calcError && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose/10 border border-rose/30">
                  <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
                  <p className="text-[12px] text-rose">{calcError}</p>
                </div>
              )}

              {!canRefresh && (
                <p className="text-[12px] text-slate leading-snug">
                  <MapPin size={12} className="inline mr-1 -mt-0.5" />
                  Set both addresses to calculate transit.
                </p>
              )}

              {/* Travel Time to Job */}
              <div className="flex flex-col gap-2 pt-1">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
                  Travel Time to Job
                </p>

                <div className="flex gap-1.5">
                  {(['one_hour_standard', 'portal_to_portal'] as TravelTimeRate[]).map(rate => (
                    <button
                      key={rate}
                      type="button"
                      onClick={() => setTravelTimeRate(rate)}
                      className={`flex-1 h-9 rounded-lg border text-[12px] font-semibold
                        transition-all duration-150
                        ${effectiveTravelTimeRate === rate
                          ? 'border-plum bg-plum/5 text-plum'
                          : 'border-stone bg-white text-midnight hover:border-plum/40'
                        }`}
                    >
                      {rate === 'one_hour_standard' ? '1 Hr Standard' : 'Portal to Portal'}
                    </button>
                  ))}
                </div>

                {isOneHourStandard && (
                  <div className="bg-mist rounded-xl px-4 py-3 flex items-center gap-3">
                    <Clock size={16} className="text-plum shrink-0" />
                    <p className="text-small font-semibold text-midnight">1 hour (standard)</p>
                  </div>
                )}

                {isPortalToPortal && (
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => setWarehouseDrawer('origin')}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-stone bg-white
                                 hover:border-plum/40 active:bg-mist transition-colors duration-150 text-left"
                    >
                      <Warehouse size={16} className="shrink-0 text-plum" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate">Origin Warehouse</p>
                        <p className="text-small font-semibold text-midnight mt-0.5 truncate">
                          {getWarehouseName(originWarehouseId)}
                        </p>
                        {originTravelSecs !== null && (
                          <p className="text-[12px] text-plum mt-0.5">
                            {fmtSecs(originTravelSecs)} to origin
                          </p>
                        )}
                      </div>
                      <ChevronRight size={16} className="shrink-0 text-slate" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setWarehouseDrawer('destination')}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-stone bg-white
                                 hover:border-plum/40 active:bg-mist transition-colors duration-150 text-left"
                    >
                      <Warehouse size={16} className="shrink-0 text-plum" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate">Destination Warehouse</p>
                        <p className="text-small font-semibold text-midnight mt-0.5 truncate">
                          {getWarehouseName(destWarehouseId)}
                        </p>
                        {destTravelSecs !== null && (
                          <p className="text-[12px] text-plum mt-0.5">
                            {fmtSecs(destTravelSecs)} to destination
                          </p>
                        )}
                      </div>
                      <ChevronRight size={16} className="shrink-0 text-slate" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {internalMove && (
            <div className="flex gap-3">
              <TransitCounter label="Distance" value="Same building" icon={<Navigation size={18} />} />
              <TransitCounter label="Est. Time" value="N/A" icon={<Clock size={18} />} />
            </div>
          )}

          {/* ── Move Type ── */}
          {(originPlace || internalMove) && (
            <div className="flex flex-col gap-1.5 pt-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Move Type</p>
              <div className="flex gap-1.5">
                {MOVE_TYPES.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { setMoveType(opt.value); }}
                    className={`flex-1 h-9 rounded-lg border text-[13px] font-medium transition-all duration-150
                      ${moveType === opt.value
                        ? 'border-plum bg-plum/5 text-plum font-semibold'
                        : 'border-stone bg-white text-midnight hover:border-plum/40'
                      }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CollapsibleSection>

        </div>{/* end left column */}

        {/* ── Right column: Origin + Destination Logistics (only in legacy view — move view has a dedicated Logistics page) ── */}
        {!isMoveView && (
        <div className="flex flex-col gap-3">

        {/* ── Origin Logistics ── */}
        <CollapsibleSection title="Origin Logistics" defaultOpen={desktopExpanded}>
          <LogisticsFlowChart
            direction="origin"
            isInternalMove={internalMove}
            value={origLogistics}
            onChange={patch => setOrigLogistics(prev => ({ ...prev, ...patch }))}
            idPrefix="ol"
          />
        </CollapsibleSection>

        {/* ── Destination Logistics (hide for internal move) ── */}
        {!internalMove && (
          <CollapsibleSection title="Destination Logistics" defaultOpen={desktopExpanded}>
            <LogisticsFlowChart
              direction="destination"
              isInternalMove={false}
              value={destLogistics}
              onChange={patch => setDestLogistics(prev => ({ ...prev, ...patch }))}
              idPrefix="dl-dest-log"
            />
          </CollapsibleSection>
        )}

        </div>
        )}{/* end right column */}

      </div>
      )}

      </div>{/* end readonly wrapper */}

      {/* Warehouse drawer */}
      {warehouseDrawer && (
        <WarehouseDrawer
          direction={warehouseDrawer}
          warehouses={warehouses}
          warehouseTransit={warehouseTransit}
          selectedId={warehouseDrawer === 'origin' ? originWarehouseId : destWarehouseId}
          onSelect={(id, secs) => selectWarehouse(warehouseDrawer, id, secs)}
          onClose={() => setWarehouseDrawer(null)}
        />
      )}

      {/* RentCast property details modal */}
      {rentCastModal === 'origin' && (originRentCast || rentCastError?.direction === 'origin') && (
        <PropertyDetailsModal
          data={originRentCast}
          error={rentCastError?.direction === 'origin' ? rentCastError.message : null}
          onClose={() => { setRentCastModal(null); setRentCastError(null); }}
        />
      )}
      {rentCastModal === 'destination' && (destRentCast || rentCastError?.direction === 'destination') && (
        <PropertyDetailsModal
          data={destRentCast}
          error={rentCastError?.direction === 'destination' ? rentCastError.message : null}
          onClose={() => { setRentCastModal(null); setRentCastError(null); }}
        />
      )}
    </div>
  );
}
